import React, { FC } from 'react';

import { AccessGrant } from '@ariksa/inventory-core';
import { Box, Center, Tr } from '@chakra-ui/react';

import { TableCell } from 'components/DataDisplay/NewTable/Table';

import { GrantNameCell, GrantNameCellProps } from './GrantNameCell';
import {
  grantCellStyles,
  isRoleGrant,
  NARROW_PADDING_X,
  renderGrantLevels,
  renderHighPrivilegesCell,
} from './utils';

interface GrantRowsProps extends Omit<GrantNameCellProps, 'grant'> {
  rowKey: string;
  grants: AccessGrant[];
}

/**
 * The grants of an expanded principal, one row each, under the principal's
 * row. The cells follow the table's columns: Principal, Mechanism, Account,
 * High Privileges and # Permissions.
 *
 * A role shows what it allows on this resource. A policy shows its levels but
 * no count, since an access line doesn't record which of its policies grants
 * which permission.
 *
 * The cells are the table's own, so expanding a row doesn't move the columns.
 */
export const GrantRows: FC<GrantRowsProps> = props => {
  const { rowKey, grants, ...rest } = props;
  return (
    <>
      {grants.map((grant, i) => {
        const isRole = isRoleGrant(grant);
        return (
          <Tr key={`${rowKey}-grant-${i}`}>
            <TableCell styles={grantCellStyles('left')} />
            <TableCell styles={grantCellStyles('left')}>
              {/* Zero width when the table sizes its columns, so a long name
                  is cut short rather than widening the column. */}
              <Box w={0} minW="full">
                <GrantNameCell grant={grant} {...rest} />
              </Box>
            </TableCell>
            <TableCell
              styles={{ ...grantCellStyles('left'), ...NARROW_PADDING_X }}
            />
            <TableCell
              styles={{ ...grantCellStyles('center'), ...NARROW_PADDING_X }}
            >
              {isRole
                ? renderHighPrivilegesCell({
                    value: grant.crud_level_counts_high ?? {},
                  })
                : renderGrantLevels(grant.crud_levels_high)}
            </TableCell>
            <TableCell
              styles={{ ...grantCellStyles('center'), ...NARROW_PADDING_X }}
            >
              <Center color={isRole ? undefined : 'gray.400'}>
                {isRole ? grant.total_permission_count ?? 0 : '-'}
              </Center>
            </TableCell>
          </Tr>
        );
      })}
    </>
  );
};
